'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Heart, Instagram, Sun, Moon } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'

export default function Footer() {
  const [currentYear, setCurrentYear] = useState(2025)
  const { theme, toggleTheme } = useTheme()

  useEffect(() => {
    setCurrentYear(new Date().getFullYear())
  }, [])

  const navigation = {
    service: [
      { name: '버디 매칭', href: '/matching/new' },
      { name: '내 매칭', href: '/matching' },
      { name: '프로필 설정', href: '/profile/edit' },
    ],
    support: [
      { name: '이용약관', href: '/terms' },
      { name: '개인정보처리방침', href: '/privacy' },
    ],
    social: [
      { name: 'Instagram', href: 'https://www.instagram.com/diving_today/', icon: Instagram },
    ]
  }

  const sitemap = [
    {
      title: '🤿 버디 매칭',
      links: [
        { name: '버디 찾기', href: '/' },
        { name: '매칭 시작', href: '/matching/new' },
        { name: '내 매칭 현황', href: '/matching' },
      ]
    },
    {
      title: '🎓 강사·투어',
      links: [
        { name: '강사 목록', href: '/instructors' },
        { name: '투어 목록', href: '/tours' },
        { name: '강사 등록', href: '/instructor/register' },
        { name: '투어 등록', href: '/tours/register' },
      ]
    },
    {
      title: '🗺️ 지도·포인트',
      links: [
        { name: '풀장 지도', href: '/map' },
        { name: '계곡 지도', href: '/map' },
        { name: '바다 포인트 지도', href: '/map' },
        { name: '포인트 제보', href: '/spots/submit' },
      ]
    },
    {
      title: '👤 마이',
      links: [
        { name: '내 프로필', href: '/profile' },
        { name: '프로필 수정', href: '/profile/edit' },
        { name: '내 예약', href: '/reservations' },
        { name: '설정', href: '/settings' },
      ]
    },
  ]

  return (
    <footer className={`relative border-t ${
      theme === 'light'
        ? 'bg-gray-50 border-gray-200'
        : 'bg-gray-900 border-gray-700'
    }`}>
      <div>
        {/* 메인 푸터 */}
        <div className="container mx-auto px-3 xxs:px-4 py-8 xxs:py-12 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xxs:gap-8">

            {/* 회사 정보 */}
            <div className="lg:col-span-1">
              <div className="flex items-center mb-4 xxs:mb-6">
                <img
                  src={theme === 'dark' ? '/logo_dark.png' : '/logo.png'}
                  alt="오늘다이빙"
                  className="h-20 xxs:h-24 w-20 xxs:w-24 object-contain"
                />
              </div>

              <p className={`text-xs xxs:text-sm xs:text-base leading-relaxed mb-4 xxs:mb-6 max-w-md break-keep ${
                theme === 'light' ? 'text-gray-700' : 'text-gray-300'
              }`}>
                다이빙 버디 매칭의 새로운 기준.
                <br />
                안전하고 즐거운 다이빙을 함께하세요.
              </p>

            </div>

            {/* 서비스 */}
            <div>
              <h3 className={`font-semibold text-sm xxs:text-base xs:text-lg mb-3 xxs:mb-4 ${
                theme === 'light' ? 'text-gray-800' : 'text-white'
              }`}>서비스</h3>
              <ul className="space-y-2 xxs:space-y-3">
                {navigation.service.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className={`transition-colors duration-200 text-xs xxs:text-sm ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-blue-600'
                          : 'text-gray-300 hover:text-blue-400'
                      }`}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* 법적 정보 */}
            <div>
              <h3 className={`font-semibold text-sm xxs:text-base xs:text-lg mb-3 xxs:mb-4 ${
                theme === 'light' ? 'text-gray-800' : 'text-white'
              }`}>법적 정보</h3>
              <ul className="space-y-2 xxs:space-y-3">
                {navigation.support.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className={`transition-colors duration-200 text-xs xxs:text-sm ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-blue-600'
                          : 'text-gray-300 hover:text-blue-400'
                      }`}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* 사이트맵 */}
        <div className={`border-t ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
          <div className="container mx-auto px-3 xxs:px-4 py-6 xxs:py-8">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
              사이트맵
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 xxs:gap-6">
              {sitemap.map((section) => (
                <div key={section.title}>
                  <p className={`text-xs font-semibold mb-2 ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
                    {section.title}
                  </p>
                  <ul className="space-y-1.5">
                    {section.links.map((link) => (
                      <li key={link.name}>
                        <Link
                          href={link.href}
                          className={`text-xs transition-colors ${
                            theme === 'light'
                              ? 'text-gray-500 hover:text-blue-600'
                              : 'text-gray-500 hover:text-blue-400'
                          }`}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 하단 구분선 */}
        <div className={`border-t ${
          theme === 'light' ? 'border-gray-300' : 'border-gray-600'
        }`}></div>

        {/* 하단 푸터 */}
        <div className="container mx-auto px-3 xxs:px-4 py-4 xxs:py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 xxs:space-y-4 md:space-y-0">

            {/* 저작권 정보 */}
            <div className={`text-xs xxs:text-sm text-center md:text-left ${
              theme === 'light' ? 'text-gray-600' : 'text-gray-300'
            }`}>
              <p className="break-keep">© {currentYear} 오늘다이빙. All rights reserved.</p>
              <p className="mt-1 flex items-center justify-center md:justify-start space-x-1">
                <span>Made with</span>
                <Heart className="w-3 xxs:w-4 h-3 xxs:h-4 text-red-400 fill-current" />
                <span>for diving lovers</span>
              </p>
            </div>

            {/* 소셜 미디어 및 문의 안내 */}
            <div className="flex flex-col items-center space-y-2 xxs:space-y-3">
              <p className={`text-xs xxs:text-sm text-center break-keep ${
                theme === 'light' ? 'text-gray-700' : 'text-gray-300'
              }`}>
                문의는 인스타 DM 으로 주세요 !
              </p>
              <div className="flex items-center gap-3 xxs:gap-4">
                {navigation.social.map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`transition-colors duration-200 hover:scale-110 transform ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-blue-600'
                          : 'text-gray-300 hover:text-blue-400'
                      }`}
                      aria-label={item.name}
                    >
                      <Icon className="w-4 xxs:w-5 h-4 xxs:h-5" />
                    </Link>
                  )
                })}

                {/* 테마 토글 버튼 */}
                <button
                  onClick={toggleTheme}
                  className={`transition-colors duration-200 hover:scale-110 transform p-1 rounded-full ${
                    theme === 'light'
                      ? 'text-gray-600 hover:text-blue-600 hover:bg-gray-100'
                      : 'text-gray-300 hover:text-blue-400 hover:bg-gray-800'
                  }`}
                  aria-label="테마 변경"
                  title={theme === 'light' ? '다크 모드로 변경' : '라이트 모드로 변경'}
                >
                  {theme === 'light' ? <Moon className="w-4 xxs:w-5 h-4 xxs:h-5" /> : <Sun className="w-4 xxs:w-5 h-4 xxs:h-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}